import React, { useState } from "react";
import { Check, X, Pencil, Tag, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import betaAPI from "v2/api";
import { showToast } from "utils/toast";
import {
  fieldLabelDisplay,
  parseFieldLabel,
  FIELD_LABEL_MAX,
  formatApiError,
} from "./constants";

/**
 * Inline editor for a wearable's field label, the number printed on the case.
 * Shows the current label big (or a "Set label" affordance when unset) and, on
 * edit, a small numeric input that PATCHes `field_label` and hands the updated
 * detail back via `onUpdated`. Reused by the bench workbench and the detail page.
 */
const FieldLabelControl = ({
  wearable,
  onUpdated,
  className = "",
  iconOnly = false,
}) => {
  const { t } = useTranslation();
  const current = fieldLabelDisplay(wearable);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);

  const start = () => {
    setValue(wearable?.field_label != null ? String(wearable.field_label) : "");
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
    setValue("");
  };

  const save = async () => {
    const { valid, value: parsed } = parseFieldLabel(value);
    if (!valid) {
      showToast.error(
        t("admin.manufacturing.fieldLabel.invalid", { max: FIELD_LABEL_MAX })
      );
      return;
    }
    setSaving(true);
    try {
      const res = await betaAPI.updateWearable({
        bleMac: wearable.ble_mac,
        body: { field_label: parsed },
      });
      showToast.success(
        parsed == null
          ? t("admin.manufacturing.fieldLabel.cleared")
          : t("admin.manufacturing.fieldLabel.updated")
      );
      onUpdated?.(res.data);
      setEditing(false);
    } catch (err) {
      showToast.error(
        formatApiError(err, t("admin.manufacturing.fieldLabel.updateFailed"))
      );
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <div className="flex items-center gap-1.5">
          <Tag size={13} className="text-[#9CA3AF]" />
          <input
            autoFocus
            type="number"
            min={0}
            max={FIELD_LABEL_MAX}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") save();
              else if (e.key === "Escape") cancel();
            }}
            placeholder="0–999"
            className="w-20 rounded-lg border border-[#E5E7EB] px-2.5 py-1.5 text-sm tabular-nums text-[#111827] outline-none focus:border-[#E07A3A]"
          />
        </div>
        <button
          onClick={save}
          disabled={saving}
          title={t("common.actions.save")}
          className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#E07A3A] text-white transition duration-150 hover:bg-[#C9682E] disabled:opacity-50"
        >
          {saving ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <Check size={14} />
          )}
        </button>
        <button
          onClick={cancel}
          disabled={saving}
          title={t("common.actions.cancel")}
          className="flex h-7 w-7 items-center justify-center rounded-lg border border-[#E5E7EB] text-[#6B7280] transition duration-150 hover:bg-[#F9FAFB] disabled:opacity-50"
        >
          <X size={14} />
        </button>
      </div>
    );
  }

  // Compact affordance for places that already show the label big (e.g. the
  // workbench top bar): a small chip, "Set label" when none, else a pencil.
  if (iconOnly) {
    return (
      <button
        onClick={start}
        title={
          current
            ? t("admin.manufacturing.fieldLabel.editPrinted")
            : t("admin.manufacturing.fieldLabel.setPrinted")
        }
        className={`group inline-flex items-center gap-1 rounded-md border border-[#E5E7EB] bg-white px-1.5 py-1 text-[#6B7280] transition duration-150 hover:border-[#F3D9C6] hover:text-[#E07A3A] ${className}`}
      >
        {current ? (
          <Pencil size={12} />
        ) : (
          <>
            <Tag size={12} />
            <span className="text-[11px] font-medium">
              {t("admin.manufacturing.fieldLabel.setLabel")}
            </span>
          </>
        )}
      </button>
    );
  }

  return (
    <button
      onClick={start}
      title={t("admin.manufacturing.fieldLabel.editPrinted")}
      className={`group flex items-center gap-2 rounded-lg text-left transition duration-150 ${className}`}
    >
      <Tag size={13} className="text-[#9CA3AF]" />
      {current ? (
        <span className="font-mono text-sm font-semibold tabular-nums text-[#111827]">
          {current}
        </span>
      ) : (
        <span className="text-sm text-[#9CA3AF]">
          {t("admin.manufacturing.fieldLabel.setLabel")}
        </span>
      )}
      <Pencil
        size={12}
        className="text-[#D1D5DB] transition-colors duration-150 group-hover:text-[#E07A3A]"
      />
    </button>
  );
};

export default FieldLabelControl;
